import React, { useMemo } from 'react';
import { Box, Form, Typography } from '@lucid/lego';
import type { FormField } from '@lucid/lego';
import type { PreferredServiceCenterOption } from './formFields';
import styles from './ServiceCenter.module.scss';

export type ServiceCenterProps = {
  handoffDateDisplay: string;
  labels: Record<string, string>;
  onComplete: (data: Record<string, unknown>) => void;
  options: PreferredServiceCenterOption[];
  selectedLocation: string;
  tableTheme: 'light' | 'dark';
  theme: 'light' | 'dark';
  warrantyStartDateDisplay: string;
  isCompleting?: boolean;
  successMessage?: string;
  errorMessage?: string;
  dataTestId?: string;
};

/**
 * Renders the preferred service center form section used in customer handoff.
 * Uses the Form component with disableSubmitUntilDirty for automatic dirty tracking.
 *
 * @component
 * @param props - Component props
 * @param props.handoffDateDisplay - Display value for handoff date
 * @param props.labels - i18n labels map
 * @param props.onComplete - Handler called with form data on submit
 * @param props.options - Dropdown options for location
 * @param props.isCompleting - Whether the save action is in progress
 * @param props.selectedLocation - Pre-selected service center location value
 * @param props.tableTheme - Inverted table/dropdown theme
 * @param props.theme - Current theme
 * @param props.successMessage - Success message to display after save
 * @param props.errorMessage - Error message to display after save failure
 * @param props.warrantyStartDateDisplay - Display value for warranty start date
 * @param props.dataTestId - Optional test ID for the component
 * @returns Preferred service center form section UI
 * @example
 * <ServiceCenter
 *   labels={{}}
 *   theme="light"
 *   tableTheme="dark"
 *   options={[]}
 *   selectedLocation="hamburg-de"
 *   onComplete={(data) => undefined}
 *   handoffDateDisplay="Dec 22, 2025"
 *   warrantyStartDateDisplay="Dec 22, 2025"
 * />
 */
const ServiceCenter: React.FC<ServiceCenterProps> = ({
  handoffDateDisplay,
  labels,
  onComplete,
  options,
  selectedLocation,
  tableTheme,
  theme,
  isCompleting = false,
  successMessage,
  errorMessage,
  dataTestId,
  warrantyStartDateDisplay,
}) => {
  const formFields = useMemo<FormField[]>(
    () => [
      {
        id: 'preferredServiceCenterLocation',
        fieldType: 'dropdown',
        name: 'preferredServiceCenterLocation',
        apiKey: 'preferredServiceCenterLocation',
        label: labels?.['serviceCenter.locationLabel'] ?? 'SERVICE CENTER LOCATION*',
        placeholder: labels?.['serviceCenter.locationPlaceholder'] ?? 'Select Location',
        defaultValue: selectedLocation,
        required: true,
        width: 'third',
        options,
        validationMessages: {
          required:
            labels?.['serviceCenter.locationRequired'] ?? 'Service center location is required',
        },
      },
      {
        id: 'datesGroup',
        fieldType: 'group',
        width: 'half',
        children: [
          {
            id: 'handoffDate',
            fieldType: 'content',
            label: labels?.['serviceCenter.handoffDate'] ?? 'Handoff Date',
            content: handoffDateDisplay || '-',
            variant: 'body',
            size: 'small',
            width: 'half',
          },
          {
            id: 'warrantyStartDate',
            fieldType: 'content',
            label: labels?.['serviceCenter.warrantyStartDate'] ?? 'Warranty Start Date',
            content: warrantyStartDateDisplay || '-',
            variant: 'body',
            size: 'small',
            width: 'half',
          },
        ],
      },
      {
        id: 'formButtons',
        fieldType: 'buttonGroup',
        align: 'left',
        buttons: [
          {
            id: 'saveButton',
            label: isCompleting
              ? (labels?.['serviceCenter.savingButton'] ?? 'SAVING...')
              : (labels?.['serviceCenter.saveButton'] ?? 'SAVE'),
            type: 'primary',
            size: 'medium',
            color: 'neutral',
            theme,
            isFluid: true,
            disabled: isCompleting,
            className: styles.completeButton,
            gaComponent: 'customer-handoff-complete',
            dataTestId: 'customer-handoff-complete-button',
          },
        ],
      },
    ],
    [
      labels,
      selectedLocation,
      options,
      isCompleting,
      theme,
      handoffDateDisplay,
      warrantyStartDateDisplay,
    ]
  );

  return (
    <Box className={styles.preferredServiceCenterContainer} data-testid={dataTestId}>
      <Typography
        variant="body"
        size="large"
        theme={theme}
        color="black"
        className={styles.preferredServiceCenterTitle}
      >
        {labels?.['serviceCenter.preferredTitle'] ?? 'Preferred Service Center'}
      </Typography>

      {successMessage && (
        <Box
          mb={16}
          pt={12}
          pb={12}
          pl={16}
          pr={16}
          className={styles.successMessageText}
          data-testid="service-center-success"
        >
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {successMessage}
          </Typography>
        </Box>
      )}

      {errorMessage && (
        <Box
          mb={16}
          pt={12}
          pb={12}
          pl={16}
          pr={16}
          className={styles.errorMessageText}
          data-testid="service-center-error"
        >
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {errorMessage}
          </Typography>
        </Box>
      )}

      <Form
        key={`service-center-form-${selectedLocation}`}
        id="service-center-form"
        theme={tableTheme}
        formFields={formFields}
        onSuccess={(data) => onComplete(data as Record<string, unknown>)}
        disableSubmitUntilDirty
        isFluid
      />
    </Box>
  );
};

ServiceCenter.displayName = 'ServiceCenter';

export default ServiceCenter;
